import { useCallback, useEffect, useState } from "react";
import {
  DeliveryFailureKind,
  type DeliveryFailure,
} from "@photography-booking/shared";
import {
  clearDeliveryIssues,
  dismissDeliveryIssue,
  listDeliveryIssues,
} from "../api/admin-client.js";
import { AdminPanel, useAdminStatus } from "./AdminPanel.js";

interface TroubleshootingAdminProps {
  token: string;
}

/** Friendly label + one-line meaning for each failure kind. */
const KIND_INFO: Record<
  DeliveryFailureKind,
  { label: string; modifier: string; meaning: string }
> = {
  [DeliveryFailureKind.BOUNCE]: {
    label: "Bounced",
    modifier: "conflict",
    meaning:
      "The email couldn't be delivered — the address may be mistyped, full, or no longer exist. Try reaching this person another way and double-check their email.",
  },
  [DeliveryFailureKind.COMPLAINT]: {
    label: "Marked as spam",
    modifier: "flagged",
    meaning:
      "The recipient marked your email as spam. Avoid emailing them again; repeated complaints can hurt whether your emails reach anyone.",
  },
  [DeliveryFailureKind.RETRY_EXHAUSTED]: {
    label: "Send failed",
    modifier: "conflict",
    meaning:
      "The email service kept failing to send this message after several tries. It may be a temporary glitch — you can follow up with the client directly.",
  },
};

/** Formats an ISO timestamp as a short, local, human-friendly date + time. */
function formatWhen(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Troubleshooting view (Req 10.4).
 *
 * Read-only list of recorded email delivery failures (bounces, spam
 * complaints, and retry-exhausted sends) from `GET /admin/delivery-issues`,
 * newest first. This is where the Owner learns that a client outcome email
 * (approval / denial / confirmation) did not actually reach its recipient, so
 * she can follow up another way — otherwise those failures are invisible.
 */
export function TroubleshootingAdmin({
  token,
}: TroubleshootingAdminProps): JSX.Element {
  const [issues, setIssues] = useState<DeliveryFailure[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const { status, reportError, reportSuccess, clearStatus } = useAdminStatus();

  const refresh = useCallback(async () => {
    setLoading(true);
    const result = await listDeliveryIssues(token);
    setLoading(false);
    if (result.ok) {
      setIssues(result.data);
    } else {
      reportError(result.message);
    }
  }, [token, reportError]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /** Dismisses one entry, dropping it from the list on success. */
  const dismiss = async (failureId: string): Promise<void> => {
    clearStatus();
    setBusy(true);
    const result = await dismissDeliveryIssue(token, failureId);
    setBusy(false);
    if (!result.ok) {
      reportError(result.message);
      return;
    }
    setIssues((current) =>
      current.filter((issue) => issue.failureId !== failureId),
    );
  };

  /** Clears every entry after a confirmation. */
  const clearAll = async (): Promise<void> => {
    if (
      typeof window !== "undefined" &&
      !window.confirm("Clear all delivery issues? This only removes the list.")
    ) {
      return;
    }
    clearStatus();
    setBusy(true);
    const result = await clearDeliveryIssues(token);
    setBusy(false);
    if (!result.ok) {
      reportError(result.message);
      return;
    }
    setIssues([]);
    reportSuccess("Delivery issues cleared.");
  };

  return (
    <AdminPanel
      title="Troubleshooting"
      description="Emails that failed to reach their recipient. If a client is listed here, they did not receive that message — follow up with them another way."
      status={status}
    >
      <div className="admin-toolbar">
        <button
          type="button"
          className="admin-button"
          onClick={() => void refresh()}
          disabled={loading || busy}
        >
          {loading ? "Refreshing\u2026" : "Refresh"}
        </button>
        {issues.length > 0 ? (
          <button
            type="button"
            className="admin-button admin-button--danger"
            onClick={() => void clearAll()}
            disabled={loading || busy}
          >
            Clear all
          </button>
        ) : null}
      </div>

      {loading && issues.length === 0 ? (
        <p className="admin-list__sub">{"Loading\u2026"}</p>
      ) : issues.length === 0 ? (
        <p className="admin-conflicts admin-conflicts--clear">
          No delivery issues — all emails are sending normally. ✓
        </p>
      ) : (
        <ul className="admin-list">
          {issues.map((issue) => {
            const info =
              KIND_INFO[issue.kind] ?? KIND_INFO[DeliveryFailureKind.RETRY_EXHAUSTED];
            return (
              <li
                key={issue.failureId}
                className="admin-list__row admin-list__row--stack"
              >
                <div className="admin-list__text">
                  <p className="admin-list__label">
                    {issue.recipient}{" "}
                    <span className={`admin-badge admin-badge--${info.modifier}`}>
                      {info.label}
                    </span>
                  </p>
                  {issue.subject ? (
                    <p className="admin-list__sub">
                      Email: {issue.subject}
                    </p>
                  ) : null}
                  <p className="admin-list__sub">{formatWhen(issue.recordedAt)}</p>
                  <p className="admin-list__message">{info.meaning}</p>
                  {issue.reason ? (
                    <p className="admin-list__sub admin-list__sub--muted">
                      Details: {issue.reason}
                      {typeof issue.attempts === "number"
                        ? ` (after ${issue.attempts} ${
                            issue.attempts === 1 ? "try" : "tries"
                          })`
                        : ""}
                    </p>
                  ) : null}
                </div>
                <div className="admin-list__actions">
                  <button
                    type="button"
                    className="admin-button"
                    disabled={busy}
                    onClick={() => void dismiss(issue.failureId)}
                    title="Remove this entry from the list"
                  >
                    Dismiss
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </AdminPanel>
  );
}
